import React from "react";

export const EMPTY_LABOR = {
  hourTypeId: "",
  hourTypeCode: "",
  hourTypeName: "",
  description: "",
  quantity: "",
  unit: "uur",
  sellRate: "",
  costRate: "",
  costInput: "",
  discount: "",
  markup: "",
  pricingSnapshot: null,
};

export const EMPTY_MATERIAL = {
  description: "",
  quantity: "",
  unit: "st",
  sellRate: "",
  costRate: "",
  costInput: "",
  discount: "",
  markup: "",
  catalogKey: "",
  categoryKey: "",
  pricingSnapshot: null,
};

export const EMPTY_FORM = {
  clientId: "",
  contactPersonId: "",
  opnameId: "",
  projectId: "",
  pricingDate: "",
  sourceType: "manual",
  sourceReference: "",
  title: "",
  version: "1",
  status: "concept",
  notes: "",
  laborLines: [{ ...EMPTY_LABOR }],
  materialLines: [{ ...EMPTY_MATERIAL }],
};

export function serializeForm(form) {
  return {
    clientId: form.clientId || undefined,
    contactPersonId: form.contactPersonId || undefined,
    opnameId: form.opnameId || undefined,
    projectId: form.projectId || undefined,
    pricingDate: form.pricingDate || undefined,
    sourceType: form.sourceType || "manual",
    sourceReference: String(form.sourceReference || "").trim(),
    title: String(form.title || "").trim(),
    version: Number(form.version || 1),
    status: form.status || "concept",
    notes: String(form.notes || "").trim(),
    laborLines: form.laborLines.map(normalizeLine),
    materialLines: form.materialLines.map(normalizeLine),
  };
}

export function normalizeLine(line) {
  return {
    hourTypeId: line.hourTypeId || undefined,
    hourTypeCode: String(line.hourTypeCode || "").trim(),
    hourTypeName: String(line.hourTypeName || "").trim(),
    description: String(line.description || "").trim(),
    quantity: Number(line.quantity || 0),
    unit: String(line.unit || "").trim(),
    sellRate: Number(line.sellRate || 0),
    costRate: Number(line.costRate || 0),
     costInput: line.costInput === "" || line.costInput === undefined ? undefined : Number(line.costInput || 0),
     discount: line.discount === "" || line.discount === undefined ? undefined : Number(line.discount || 0),
     markup: line.markup === "" || line.markup === undefined ? undefined : Number(line.markup || 0),
     catalogKey: String(line.catalogKey || "").trim(),
     categoryKey: String(line.categoryKey || "").trim(),
     pricingSnapshot: line.pricingSnapshot || undefined,
  };
}

export function computeTotals(form) {
  const laborLines = Array.isArray(form.laborLines) ? form.laborLines : [];
  const materialLines = Array.isArray(form.materialLines) ? form.materialLines : [];
  const laborHours = laborLines.reduce((sum, line) => sum + Number(line.quantity || 0), 0);
  const laborSell = laborLines.reduce(
    (sum, line) => sum + Number(line.quantity || 0) * Number(line.sellRate || 0),
    0
  );
  const laborCost = laborLines.reduce(
    (sum, line) => sum + Number(line.quantity || 0) * Number(line.costRate || 0),
    0
  );
  const materialSell = materialLines.reduce(
    (sum, line) => sum + Number(line.quantity || 0) * Number(line.sellRate || 0),
    0
  );
  const materialCost = materialLines.reduce(
    (sum, line) => sum + Number(line.quantity || 0) * Number(line.costRate || 0),
    0
  );
  const otherSell = 0;
  const otherCost = 0;
  const totalSell = laborSell + materialSell + otherSell;
  const totalCost = laborCost + materialCost + otherCost;
  const margin = totalSell - totalCost;
  const marginPct = totalSell > 0 ? (margin / totalSell) * 100 : 0;

  return {
    laborHours,
    laborSell,
    laborCost,
    materialSell,
    materialCost,
    otherSell,
    otherCost,
    totalSell,
    totalCost,
    margin,
    marginPct,
  };
}

export function computeSectionTotals(lines = []) {
  const cost = lines.reduce(
    (sum, line) => sum + Number(line.quantity || 0) * Number(line.costRate || 0),
    0
  );
  const sell = lines.reduce(
    (sum, line) => sum + Number(line.quantity || 0) * Number(line.sellRate || 0),
    0
  );
  const margin = sell - cost;
  return { cost, sell, margin };
}

export function toMoney(value) {
  return Number(value || 0).toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function toNumberInputValue(value) {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) && numeric !== 0 ? String(numeric) : "";
}

export function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("nl-NL");
}

export function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function getProjectNumber(project) {
  return project?.projectNummer || project?.number || "-";
}

export function getProjectName(project) {
  return project?.naam || project?.title || "Project";
}

export function getClientName(client) {
  return client?.naam || client?.name || "-";
}

export function getClientNumber(client) {
  return client?.klantnummer || client?.customerNumber || "-";
}

export function getProjectClientName(project) {
  return (
    project?.clientName
    || project?.clientNaam
    || project?.client?.name
    || project?.client?.naam
    || project?.clientId?.name
    || project?.clientId?.naam
    || "-"
  );
}

export function getCalculatieClientName(item = {}) {
  return (
    item?.clientId?.naam
    || item?.clientId?.name
    || item?.clientSnapshot?.name
    || "-"
  );
}

export function getCalculatieContactName(item = {}) {
  return item?.contactSnapshot?.name || "-";
}

export function getCalculatieOpnameTitle(item = {}) {
  return item?.opnameId?.titel || item?.opnameId?.title || item?.opnameSnapshot?.title || "-";
}

export function getSourceTypeLabel(value = "") {
  switch (String(value || "").trim().toLowerCase()) {
    case "website":
      return "Website";
    case "customer_portal":
      return "Klantportaal";
    case "opname":
      return "Opname";
    case "project":
      return "Project";
    default:
      return "Handmatig";
  }
}

export function getCalculatieCloneTypeLabel(value = "") {
  switch (String(value || "").trim().toLowerCase()) {
    case "revision":
      return "Revisie";
    case "duplicate_same_client":
      return "Duplicaat zelfde klant";
    case "copy_other_client":
      return "Kopie andere klant";
    case "copy":
      return "Revisie";
    case "duplicate":
      return "Kopie andere klant";
    default:
      return "Origineel";
  }
}

export function getCalculatieSeriesCode(item = {}) {
  const value = String(item?.versionSeriesId || item?._id || "").trim();
  if (!value) return "-";
  return `SER-${value.slice(-6).toUpperCase()}`;
}

export function getCalculatieDocumentNumber(item, editingId = "") {
  const sourceId = String(item?._id || editingId || "").trim();
  if (!sourceId) return "CAL-CONCEPT";
  return `CAL-${sourceId.slice(-6).toUpperCase()}`;
}

export function mapCalculatieToForm(item) {
  return {
    clientId: String(item?.clientId?._id || item?.clientId || ""),
    contactPersonId: String(item?.contactPersonId || ""),
    opnameId: String(item?.opnameId?._id || item?.opnameId || ""),
    projectId: String(item?.projectId?._id || item?.projectId || ""),
    pricingDate: item?.pricingDate ? String(item.pricingDate).slice(0, 10) : "",
    sourceType: item?.sourceType || "manual",
    sourceReference: item?.sourceReference || "",
    title: item.title || "",
    version: String(item.version || 1),
    status: item.status || "concept",
    notes: item.notes || "",
    laborLines: (Array.isArray(item.laborLines) && item.laborLines.length > 0
      ? item.laborLines
      : [{ ...EMPTY_LABOR }]).map((line) => ({
      hourTypeId: String(line.hourTypeId || ""),
      hourTypeCode: line.hourTypeCode || "",
      hourTypeName: line.hourTypeName || "",
      description: line.description || "",
      quantity: toNumberInputValue(line.quantity),
      unit: line.unit || "uur",
      sellRate: toNumberInputValue(line.sellRate),
      costRate: toNumberInputValue(line.costRate),
      costInput: toNumberInputValue(line.costInput),
      discount: toNumberInputValue(line.discount),
      markup: toNumberInputValue(line.markup),
      pricingSnapshot: line.pricingSnapshot || null,
    })),
    materialLines: (Array.isArray(item.materialLines) && item.materialLines.length > 0
      ? item.materialLines
      : [{ ...EMPTY_MATERIAL }]).map((line) => ({
      description: line.description || "",
      quantity: toNumberInputValue(line.quantity),
      unit: line.unit || "st",
      sellRate: toNumberInputValue(line.sellRate),
      costRate: toNumberInputValue(line.costRate),
      costInput: toNumberInputValue(line.costInput),
      discount: toNumberInputValue(line.discount),
      markup: toNumberInputValue(line.markup),
      catalogKey: line.catalogKey || "",
      categoryKey: line.categoryKey || "",
      articleCode: line.articleCode || line.supplierArticleNumber || "",
      pricingSnapshot: line.pricingSnapshot || null,
    })),
  };
}

export function buildValidationItems({ form, totals, editingId, selectedProject, selectedCalculatie, showFinancials = true }) {
  const items = [];
  const laborLines = Array.isArray(form.laborLines) ? form.laborLines : [];
  const materialLines = Array.isArray(form.materialLines) ? form.materialLines : [];
  const combined = [
    ...laborLines.map((line, index) => ({ section: "Arbeid", line, index: index + 1 })),
    ...materialLines.map((line, index) => ({ section: "Materiaal", line, index: index + 1 })),
  ];

  if (!form.projectId) {
    items.push({
      tone: "warning",
      label: "Nog geen project gekoppeld",
      detail: "Dat is toegestaan in fase 0. Projectkoppeling blijft optioneel tot na offerte/akkoord.",
    });
  }

  if (!form.clientId) {
    items.push({
      tone: "danger",
      label: "Geen relatie gekoppeld",
      detail: "Koppel minimaal een klant/opdrachtgever als primaire calculatiecontext.",
    });
  }

  combined.forEach(({ section, line, index }) => {
    if (!String(line.description || "").trim()) {
      items.push({
        tone: "warning",
        label: `${section}regel ${index} zonder omschrijving`,
        detail: "Voeg een duidelijke omschrijving toe voor overdracht en controle.",
      });
    }
    if (!Number(line.quantity || 0)) {
      items.push({
        tone: "warning",
        label: `${section}regel ${index} heeft geen aantal`,
        detail: "Regels met 0 of leeg aantal tellen niet mee in de calculatie.",
      });
    }
    if (showFinancials && Number(line.sellRate || 0) < Number(line.costRate || 0)) {
      items.push({
        tone: "danger",
        label: `${section}regel ${index} heeft negatieve marge`,
        detail: "Verkoop ligt lager dan kostprijs op regelniveau.",
      });
    }
  });

  if (showFinancials && totals.margin < 0) {
    items.push({ tone: "danger", label: "Totale marge is negatief", detail: "Controleer tarieven, aantallen en kostprijzen voordat je doorzet." });
  }

  if (editingId && form.projectId && !selectedCalculatie?.appliedToProjectAt) {
    items.push({ tone: "warning", label: "Nog niet toegepast op projectbudget", detail: "Projectbudget is nog niet gesynchroniseerd met deze calculatie." });
  }

  if (editingId && form.projectId && !selectedProject) {
    items.push({ tone: "warning", label: "Projectcontext onvolledig", detail: "Projectgegevens voor header en controle zijn niet volledig geladen." });
  }

  items.push({ tone: "neutral", label: "Prijsbron per regel", detail: "Gebruik de bronbadges en previewacties om snapshots en actuele prijsresolutie te vergelijken." });

  return items;
}

export function getAppliedBadgeMeta(item) {
  if (item?.appliedToProjectAt) {
    return { tone: "success", label: "Ja" };
  }
  return { tone: "warning", label: "Nee" };
}

export function compactLabel(label) {
  return React.createElement("span", { className: "truncate" }, label);
}
